import React from "react";
import { graphql } from "gatsby";
import Img from "gatsby-image";

import Layout from "../components/Layout";
import SearchEngineOptimization from "../components/SEO";
import HeroSplit from "../components/Hero/HeroSplit";
import WhyUs from "../components/Repeating/WhyUs";
import SliderTestimonials from "../components/Slider/SliderTestimonials";
import About from "../components/Repeating/About";
import CallToAction from "../components/Repeating/CallToAction";
import FaqSection from "../components/Repeating/FaqSection";

const faqGroup = {
  title: "Home Insurance FAQs",
  faqs: [
    {
      title: "1. What is home insurance?",
      body: "Home insurance provides coverage to protect your home, personal belongings, and other property, as well as liability protection for visitors and neighbors. It safeguards you from losses due to theft, fire, flood, and other covered events.",
    },
    {
      title: "2. Who needs home insurance in Chula Vista and San Diego?",
      body: "Any homeowner or property owner in Chula Vista, San Diego, or surrounding areas should have home insurance. It protects your investment and provides financial security in case of unexpected damage or loss.",
    },
    {
      title: "3. What does home insurance cover?",
      body: "Home insurance can cover building damage, personal property loss, personal liability, other structures on your property, medical payments for visitors, and additional living expenses if you cannot stay in your home.",
    },
    {
      title: "4. How does Hoffman Hanono approach home insurance?",
      body: "Hoffman Hanono works with you to find the best home insurance solution for your property and needs. They partner with trusted carriers to provide tailored coverage and help you secure better premiums.",
    },
    {
      title: "5. Why choose Hoffman Hanono for home insurance in San Diego?",
      body: "With over 50 years of experience, Hoffman Hanono is a family-owned agency with deep local knowledge. Their personalized service and long-standing relationships with carriers make them a trusted partner for homeowners.",
    },
    {
      title: "6. How can I get a quote for home insurance?",
      body: "You can request a quote online, call (619) 420-1861, or email info@hhinsurance.com. Their agents will help you evaluate coverage options and find the right policy for your home.",
    },
  ],
};

const Page = ({ data }) => {
  const heroImages = [
    data.heroDesktop.childImageSharp.fixed,
    {
      ...data.heroMobile.childImageSharp.fixed,
      media: `(max-width: 767px)`,
    },
  ];

  return (
    <Layout>
      <SearchEngineOptimization
        title="Home Insurance Chula Vista | Hoffman Hanono"
        description="If you need home insurance in Chula Vista & beyond, look no further. Hoffman Hanono gets you the right coverage for your needs. Get a free quote!"
        openGraphImage={data.openGraphImage.publicURL}
        twitterOpenGraphImage={data.twitterOpenGraphImage.publicURL}
      />

      <HeroSplit
        images={heroImages}
        position="50% 50%"
        maxHeightDesktop="618px"
        maxHeightMobile="500px"
      >
        <div className="max-w-2xl">
          <h1>Home Insurance in Chula Vista & Beyond</h1>
          <p className="mb-0">
            You’ve invested in a home. Now keep it safe with homeowners
            insurance suited to your place. With home insurance, get coverage to
            protect not just your home and personal belongings but your visitors
            and other property owners as well. Hoffman Hanono connects you with
            the best coverage for risks and damages such as theft, fire, flood,
            and more.
          </p>
        </div>
      </HeroSplit>

      <section className="wrapper">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-y-0 md:gap-x-24 items-center">
            <div className="md:col-start-1 md:col-end-7">
              <Img fluid={data.whatsIncluded.childImageSharp.fluid} />
            </div>
            <div className="md:col-end-13 md:col-span-6">
              <h2>The Best Premiums for Your Needs</h2>
              <p>
                Your home is unique and your home insurance policy should be,
                too. Here are some standard types of home insurance coverage
                from our trusted carriers:
              </p>
              <ul className="styled-list">
                <li>Building Coverage</li>
                <li>Personal Property Coverage</li>
                <li>Personal Liability Coverage</li>
                <li>Other Structures Coverage</li>
                <li>Medical Payments Coverage</li>
                <li>Loss-of-Use Coverage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <section className="wrapper bg-gray-100 pt-24 pb-20 md:py-32 px-6 rounded-xl md:rounded-3xl">
          <div className="max-w-4xl mx-auto">
            <p className="decorative-text">Custom Solutions</p>
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-6">
              <div>
                <h2>Our Homeowners Insurance Approach</h2>
              </div>
              <div>
                <p className="mb-0">
                  Whether you are managing risk for business or personal assets,
                  Hoffman Hanono Insurance Services is here to advise and
                  partner with you to find insurance solutions that are meant
                  entirely for you. After all, insurance isn’t
                  “one-size-fits-all.” We’ll only place you where you belong.
                  Not only that, but we provide quality service and resources
                  that will help you secure better insurance premiums in the
                  long run. This commitment reveals why we've been a pillar of
                  the San Diego and Chula Vista insurance community since 1959.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <WhyUs />
      <SliderTestimonials />
      <FaqSection faqGroup={faqGroup} isHardcoded={true} />
      <About />
      <CallToAction />
    </Layout>
  );
};

export default Page;

export const query = graphql`
  {
    openGraphImage: file(
      relativePath: { eq: "open-graph/facebook/homeowners-FB.jpg" }
    ) {
      publicURL
    }
    twitterOpenGraphImage: file(
      relativePath: { eq: "open-graph/twitter/homeowners-twitter.jpg" }
    ) {
      publicURL
    }
    heroDesktop: file(
      relativePath: {
        eq: "personal-insurance/home-insurance/homeowners-hero-desktop.jpg"
      }
    ) {
      childImageSharp {
        fixed(width: 548, height: 618) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    heroMobile: file(
      relativePath: {
        eq: "personal-insurance/home-insurance/homeowners-hero-mobile.jpg"
      }
    ) {
      childImageSharp {
        fixed(width: 748) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    whatsIncluded: file(
      relativePath: {
        eq: "personal-insurance/home-insurance/whats-included.jpg"
      }
    ) {
      childImageSharp {
        fluid(maxWidth: 560) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`;
